import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { VOICES, getVoice, setVoice } from "../lib/voicePref";
import { Play, Check } from "lucide-react";
import { toast } from "sonner";

const PREVIEW_TEXT = "Good morning Antwan. Here's the shape of today.";

const VoiceSelector = () => {
  const [current, setCurrent] = useState(getVoice());
  const [playing, setPlaying] = useState(null);
  const [previewText, setPreviewText] = useState(PREVIEW_TEXT);
  const audioRef = useRef(null);

  const pick = (id) => {
    setVoice(id);
    setCurrent(id);
    toast("Voice updated", { description: id.toUpperCase() });
  };

  const preview = async (id) => {
    setPlaying(id);
    try {
      const blob = await api.assistantTTS(previewText.slice(0, 200), id);
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => setPlaying(null);
        await audioRef.current.play();
      }
    } catch (e) {
      toast("Preview failed", { description: e?.response?.data?.detail || "" });
      setPlaying(null);
    }
  };

  return (
    <section data-testid="voice-selector">
      <SectionHeader
        code="04"
        label="SALLY · VOICE"
        caption="Which voice speaks your briefings. Preview each, then pick her tone."
      />
      <div className="op-panel p-6 md:p-8 space-y-5">
        <div>
          <div className="op-microcopy mb-2">PREVIEW LINE</div>
          <input
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            className="op-input"
            data-testid="voice-preview-text"
            maxLength={200}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {VOICES.map((v) => {
            const active = current === v.id;
            const isPlaying = playing === v.id;
            return (
              <div
                key={v.id}
                data-testid={`voice-row-${v.id}`}
                className={`flex items-center justify-between p-4 border transition-colors ${
                  active
                    ? "border-[#00E5FF] bg-[rgba(0,229,255,0.04)]"
                    : "border-white/[0.06] hover:border-white/15"
                }`}
              >
                <div>
                  <div className="text-white text-sm font-medium mb-0.5 flex items-center gap-2">
                    {v.label}
                    {active && (
                      <span className="op-tag text-[#00E5FF] inline-flex items-center gap-1">
                        <Check size={10} /> ACTIVE
                      </span>
                    )}
                  </div>
                  <div className="op-tag !normal-case tracking-normal text-[#A0A5B5]">{v.note}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => preview(v.id)}
                    disabled={!!playing}
                    data-testid={`voice-preview-${v.id}`}
                    className="op-btn text-[10px] px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <Play size={10} />
                    {isPlaying ? "PLAYING…" : "PREVIEW"}
                  </button>
                  {!active && (
                    <button
                      onClick={() => pick(v.id)}
                      data-testid={`voice-pick-${v.id}`}
                      className="op-btn op-btn--primary text-[10px] px-3 py-1.5"
                    >
                      USE
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <audio ref={audioRef} className="hidden" data-testid="voice-preview-audio" />
      </div>
    </section>
  );
};

export default VoiceSelector;
